import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useRef, useMemo } from 'react';
import { styled } from '../../styles';
import { Column, Row } from '../layout';
import { AppText } from '../typography';
import { Avatar, AvatarIcon } from '../avatar/index';
import { colors } from '../../styles/theme';
import { formatTranscriptionTimestamp } from '@streem/toolbox';
import { TRANSCRIPT_BLOCK } from '../notes/consts';
import { splitTextBySearchTerms } from '../../util/transcript_utils';
export const TranscriptItem = ({ transcriptBlock, index, isSelected, autoScrollModeActive, handleTimeStampJump, searchValue, recordClickEvent, matchingSearchTerms, }) => {
    const containerRef = useRef(null);
    const handleTranscriptElementClick = () => {
        handleTimeStampJump(transcriptBlock.startTimeMs);
        recordClickEvent(TRANSCRIPT_BLOCK);
    };
    useEffect(() => {
        // if the container is selected, scroll it into view
        autoScrollModeActive &&
            isSelected &&
            containerRef.current &&
            containerRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                // only scroll the nearest container.  (Available with Chrome 140.)
                container: 'nearest',
            }); // our TS version doesn't know about the container option yet
    }, [isSelected, autoScrollModeActive, containerRef]);
    const avatarDataTestId = `transcript-speaker-avatar-${index}`;
    return (_jsxs(TranscriptItemContainer, { isSelected: isSelected, "data-testid": `transcription-block-${index}`, onClick: handleTranscriptElementClick, ref: containerRef, children: [_jsxs(TranscriptItemHeader, { children: [_jsxs(SpeakerInfo, { children: [transcriptBlock.avatarUrl ? (_jsx(Avatar, { displayName: transcriptBlock.speakerName, size: "xsmall", backgroundColor: colors.blue30, src: transcriptBlock.avatarUrl, "data-testid": avatarDataTestId })) : (_jsx(AvatarIcon, { size: "xsmall", backgroundColor: colors.blue30, icon: "PersonIcon", "data-testid": avatarDataTestId, fillColor: colors.grey50 })), _jsx(AppText, { "data-testid": `speaker-name-${index}`, semibold: true, headingFontFamily: true, children: transcriptBlock.speakerName })] }), _jsx(AppText, { "data-testid": "timestamp", headingFontFamily: true, color: "grey40", style: {
                            cursor: 'pointer',
                        }, onClick: () => handleTimeStampJump(transcriptBlock.startTimeMs), children: formatTranscriptionTimestamp(transcriptBlock.startTimeMs) })] }), _jsx(TranscriptText, { searchQuery: searchValue, text: transcriptBlock.text, isSelected: isSelected, matchingSearchTerms: matchingSearchTerms, segmentIndex: index })] }));
};
const TranscriptItemContainer = styled(Column) `
    align-items: flex-start;
    padding: 0rem;
    gap: 0.625rem;
    width: 100%;
`;
const TranscriptItemHeader = styled(Row) `
    justify-content: space-between;
    align-items: center;
    padding-right: 0.5rem;
    gap: 0.625rem;
    width: 100%;
    height: 2.6rem;
    margin-bottom: 0.375rem;
`;
const SpeakerInfo = styled(Row) `
    align-items: center;
    padding: 0px;
    gap: 0.5rem;
    height: 2rem;
`;
export const TranscriptText = ({ text, searchQuery, isSelected, matchingSearchTerms, segmentIndex, }) => {
    const highlightedContent = useMemo(() => {
        if (!searchQuery || !text || matchingSearchTerms.length === 0) {
            return _jsx("span", { children: text });
        }
        const parts = splitTextBySearchTerms(text, matchingSearchTerms);
        return (_jsx("span", { children: parts.map((part, index) => {
                // Check if this part matches the search query (case-insensitive)
                const isMatch = part.match;
                return isMatch ? (_jsx(Highlight, { "data-testid": `highlight-${searchQuery}`, children: part.text }, index)) : (_jsx("span", { children: part.text }, index));
            }) }));
    }, [text, searchQuery, matchingSearchTerms]);
    return (_jsx(TextWrapper, { "data-testid": `transcript-text-${segmentIndex}`, isSelected: isSelected, children: highlightedContent }));
};
const Highlight = styled.mark(({ theme }) => ({
    backgroundColor: theme.colors.blue30,
    color: theme.colors.grey90,
}));
const TextWrapper = styled(AppText)(({ theme, isSelected }) => ({
    fontSize: '0.875rem',
    backgroundColor: isSelected ? theme.colors.grey05 : undefined,
    width: '100%',
    display: 'block',
    padding: '0rem 0rem 0rem 0.5rem',
    borderRadius: isSelected ? '0.25rem' : '0',
    border: isSelected ? `.063rem solid ${theme.colors.grey30}` : 'none',
    ':hover': {
        cursor: 'pointer',
    },
}));
//# sourceMappingURL=transcript_item.js.map